<template>
  <div
    class="tag-item"
    :class="{
      active: localValue.isActive,
      toggleable: isToggleEnabled,
      'is-processing': isProcessing
    }"
  >
    <div class="tag-item__content" @click="tagClickHandler">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div class="tag-name" v-bind="attrs" v-on="on">
            <v-icon
              v-if="
                localValue.isAutoAssigned ||
                  (showAutoCategorizationIcon && tag.is_auto_categorized)
              "
              color="black"
              class="mr-2"
              >icon-robot</v-icon
            >
          </div>
        </template>

        <span>
          {{
            localValue.isAutoAssigned
              ? $t("message.reviews.autCategorizationEnabled")
              : $t("message.reviews.autoCategorizationLabel")
          }}
        </span>
      </v-tooltip>

      <v-tooltip top :disabled="!isTextOverflow">
        <template v-slot:activator="{ on, attrs }">
          <div class="tag-name" v-bind="attrs" v-on="on">
            <span v-if="!isTextOverflow">{{ tag.name }}</span>
            <span v-else>{{ tag.name.substr(0, maxCharacters) + "..." }}</span>
          </div>
        </template>

        <span>
          {{ tag.name }}
        </span>
      </v-tooltip>

      <v-menu
        v-if="
          !tag.categorization_in_progress &&
            managable &&
            autoCategorizationSettings
        "
        ref="menu"
        v-model="menu"
        offset-y
        max-width="260px"
        rounded="0"
      >
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-icon size="24">
              icon-more
            </v-icon>
          </div>
        </template>
        <div class="dropdown">
          <v-card outlined tile>
            <ul class="dropdown-list">
              <li
                v-if="
                  autoCategorizationSettings.isVisible &&
                    canUseAiReviewsCategorization
                "
                class="dropdown-list__el"
              >
                <v-icon size="24" color="black">
                  icon-robot
                </v-icon>
                <span class="dropdown-list__label">
                  {{ $t("message.reviews.autoCategorization") }}
                </span>
                <v-switch
                  v-model="isAuto"
                  :disabled="autoCategorizationSettings.isDisabled"
                  hide-details
                  inset
                  @change="changeAuto"
                ></v-switch>
              </li>
              <li v-if="editEnabled" class="dropdown-list__el" @click="onEdit">
                <v-icon size="24" color="black">
                  icon-edit
                </v-icon>
                {{ $t("message.common.changeName") }}
              </li>
              <li
                v-if="removeEnabled"
                class="dropdown-list__el"
                @click="onRemove"
              >
                <v-icon size="24" color="black">
                  icon-close
                </v-icon>
                {{ $t("message.common.delete") }}
              </li>
            </ul>
          </v-card>
        </div>
      </v-menu>
    </div>
    <TagsManagerListSentimentSelector
      v-if="canUseAiSentimentAnalysis && showSentimentSelector"
      :read-only="!isToggleEnabled"
      :active="localValue.isActive"
      :sentiment="localValue.sentiment"
      @change-sentiment="onChange(true, $event)"
      @clear-selection="onClearSelection(false, undefined)"
    />
  </div>
</template>

<script>
import TagsManagerListSentimentSelector from "./TagsManagerListSentimentSelector.vue";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";

export default {
  name: "TagsManagerListItem",
  components: {
    TagsManagerListSentimentSelector
  },
  model: {
    prop: "active"
  },
  props: {
    active: {
      type: Object,
      default: () => {
        return {
          isActive: false,
          sentiment: null,
          isAutoAssigned: false
        };
      }
    },
    tag: {
      type: Object,
      required: true
    },
    showSentimentSelector: {
      type: Boolean,
      default: true
    },
    toggleEnabled: {
      type: Boolean,
      default: false
    },
    editEnabled: {
      type: Boolean,
      default: false
    },
    removeEnabled: {
      type: Boolean,
      default: false
    },
    maxCharacters: {
      type: Number,
      default: 0
    },
    managable: {
      type: Boolean,
      default: false
    },

    isProcessing: {
      type: Boolean,
      default: false
    },
    autoCategorizationSettings: {
      type: Object
    },
    showAutoCategorizationIcon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return { menu: false, isAuto: false, localValue: null };
  },
  computed: {
    canUseAiReviewsCategorization() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_USE_AI_REVIEWS_CATEGORIZATION
      );
    },
    canUseAiSentimentAnalysis() {
      return (
        planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_SENTIMENT_ANALYSIS) ||
        planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_MANUALLY_ADD_SENTIMENT)
      );
    },
    isTextOverflow() {
      return !!this.maxCharacters && this.tag.name.length > this.maxCharacters;
    },
    isToggleEnabled() {
      return !!(this.toggleEnabled && !this.isProcessing);
    }
  },
  created() {
    this.localValue = this.active;
  },
  watch: {
    active: {
      handler(current) {
        this.localValue = current;
      }
    },
    tag: {
      immediate: true,
      handler(current, last) {
        if (!current.hasOwnProperty("is_auto_categorized")) return;
        if (
          (!last && current.is_auto_categorized) ||
          (last && current.is_auto_categorized !== last.is_auto_categorized)
        ) {
          this.isAuto = current.is_auto_categorized;
        }
      }
    }
  },

  methods: {
    tagClickHandler() {
      if (!this.isToggleEnabled) return;
      if (!this.localValue.isActive) {
        this.onChange(!this.localValue.isActive, this.localValue.sentiment);
        return;
      }
      this.onClearSelection();
    },
    changeAuto(current) {
      this.tag.is_auto_categorized = current;
      this.$emit("change-auto", this.tag);
    },
    onChange(isActive, sentiment = null) {
      this.$emit("change", { ...this.localValue, isActive, sentiment });
    },
    onClearSelection() {
      this.$emit("change", {
        ...this.localValue,
        isActive: false,
        sentiment: undefined
      });
    },
    onEdit() {
      this.$emit("edit", this.tag);
    },
    onSentimentChange(event) {
      this.$emit("change-sentiment", event);
    },
    onRemove() {
      this.$emit("remove", this.tag);
    }
  }
};
</script>

<style lang="scss" scoped>
.tag-item {
  display: flex;
  align-items: center;
  background: $light-4;
  border-radius: $container-border-radius;
  margin: $spacing * 0.625;
  height: 46px;

  &__content {
    padding: 0 $spacing * 2;
    display: flex;
    align-items: center;
  }

  &.is-processing {
    opacity: 0.5;
  }

  &.active {
    background: $primary;

    ::v-deep.v-icon:before,
    .tag-name {
      color: $light;
    }
  }

  &.toggleable {
    cursor: pointer;
    user-select: none;
  }
}

::v-deep .tag-name {
  @include font-size(14px);
  color: $dark-blue-3;
  font-weight: 500;
  text-overflow: ellipsis;
}

.action.v-icon {
  opacity: 0.5;
}

.mr {
  margin-right: $spacing * 1.5;
}

.ml {
  margin-left: $spacing * 1.5;
}
.dropdown {
  overflow: hidden;
  ::v-deep.v-card {
    border-radius: 10px !important;
    overflow: hidden;
  }
}

.dropdown-list {
  padding: 0;

  &__label {
    max-width: 150px;
  }

  &__el {
    display: flex;
    padding: $spacing * 2.5;
    border-bottom: 1px solid $gray-300;
    cursor: pointer;
    align-items: center;
    @include font-size(14px);
    &:hover {
      background: $gray-200;
    }

    ::v-deep.v-icon {
      margin-right: $spacing * 1.5;
    }

    ::v-deep.v-input {
      margin: 0 0 0 $spacing * 1.5;
    }
  }
}
</style>
